import { useRef, useState, type ChangeEvent, type DragEvent } from "react";
import { FileSpreadsheet, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  MAX_PROVIDER_CSV_FILE_SIZE_BYTES,
  PROVIDER_CSV_ACCEPT,
} from "./provider-csv-import";

type CsvFileDropzoneProps = {
  file: File | null;
  disabled?: boolean;
  onFile: (file: File | null) => void;
  onError: (message: string) => void;
};

const validateCsvFile = (file: File): string | null => {
  if (!file.name.toLowerCase().endsWith(".csv")) {
    return "Please upload a CSV file.";
  }
  if (file.size === 0) {
    return "The selected CSV is empty.";
  }
  if (file.size > MAX_PROVIDER_CSV_FILE_SIZE_BYTES) {
    return "The CSV must be 1 MB or smaller.";
  }
  return null;
};

export function CsvFileDropzone({
  file,
  disabled = false,
  onFile,
  onError,
}: CsvFileDropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const chooseFile = (nextFile: File | undefined) => {
    if (!nextFile) {
      return;
    }
    const error = validateCsvFile(nextFile);
    if (error) {
      onError(error);
      return;
    }
    onFile(nextFile);
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragging(false);
    if (!disabled) {
      chooseFile(event.dataTransfer.files[0]);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    chooseFile(event.target.files?.[0]);
    event.target.value = "";
  };

  if (file) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-blue-100">
          <FileSpreadsheet className="h-5 w-5 text-blue-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-gray-800">
            {file.name}
          </p>
          <p className="text-xs text-gray-500">
            {(file.size / 1024).toFixed(0)} KB
          </p>
        </div>
        <button
          type="button"
          disabled={disabled}
          onClick={() => onFile(null)}
          className="shrink-0 text-gray-400 hover:text-gray-600 disabled:opacity-50"
          aria-label="Remove CSV"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    );
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-disabled={disabled}
      onDragOver={(event) => {
        event.preventDefault();
        event.stopPropagation();
        if (!disabled) {
          setIsDragging(true);
        }
      }}
      onDragLeave={() => setIsDragging(false)}
      onDrop={handleDrop}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (!disabled && (event.key === "Enter" || event.key === " ")) {
          inputRef.current?.click();
        }
      }}
      className={cn(
        "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed bg-gray-50 px-4 py-8 text-center transition-colors",
        isDragging
          ? "border-blue-400 bg-blue-50"
          : "border-gray-200 hover:border-blue-300 hover:bg-blue-50/50",
        disabled && "cursor-not-allowed opacity-60",
      )}
    >
      <Upload className="h-6 w-6 text-gray-400" />
      <p className="text-sm text-gray-600">
        Drag and drop a CSV here, or{" "}
        <span className="font-medium text-blue-600">browse</span>
      </p>
      <p className="text-xs text-gray-400">CSV only, up to 1 MB</p>
      <input
        ref={inputRef}
        type="file"
        accept={PROVIDER_CSV_ACCEPT}
        className="hidden"
        disabled={disabled}
        onChange={handleChange}
      />
    </div>
  );
}
